import { useLayoutEffect, useRef } from "react";

export const useResizeObserver = (
  onResize: (size: { width: number; height: number }) => void
) => {
  const rafRef = useRef<number>();
  const ref = useRef<HTMLDivElement>(null);
  const previousSizeRef = useRef<{ width: number; height: number } | null>(
    null
  );
  const isInitialObservationRef = useRef(true);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const { width, height } = entries[0].target.getBoundingClientRect();
        const newSize = { width, height };

        if (isInitialObservationRef.current) {
          isInitialObservationRef.current = false;
          previousSizeRef.current = newSize;
          onResize(newSize);
          return;
        }

        if (
          !previousSizeRef.current ||
          previousSizeRef.current.width !== width ||
          previousSizeRef.current.height !== height
        ) {
          previousSizeRef.current = newSize;
          onResize(newSize);
        }
      });
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      isInitialObservationRef.current = true;
    };
  }, [onResize]);

  return ref;
};
